import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import { useConsents } from "../hooks/useConsents";
import { validateEmail } from "../utils/validation";
import DisabledFeature from "./DisabledFeature";
import UpgradePrompt from "./UpgradePrompt";
import "../styles/EmailAccounts.css";

// Providers that connect over IMAP but require an APP PASSWORD (not the
// account's normal password, and not OAuth). Their IMAP host/port are known
// server-side, so the user never needs to enter them.
const APP_PASSWORD_HELP: Record<string, { url: string; note: string }> = {
  yahoo: {
    url: "https://login.yahoo.com/account/security/app-passwords",
    note: "Yahoo needs an app password, not your normal password.",
  },
  icloud: {
    url: "https://account.apple.com",
    note: "iCloud needs an app-specific password, not your Apple ID password.",
  },
};

interface EmailAccount {
  id: string;
  provider: string;
  email: string;
  isConnected: boolean;
  lastSyncAt: string | null;
  createdAt: string;
}

interface EmailProvider {
  id: string;
  name: string;
  type: "oauth" | "imap";
  icon: string;
}

const EmailAccounts: React.FC = () => {
  const navigate = useNavigate();
  const { consents, loading: consentsLoading } = useConsents();
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const [showProviderSelector, setShowProviderSelector] = useState(false);
  const [showIMAPForm, setShowIMAPForm] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<EmailProvider | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [imapFormData, setImapFormData] = useState({
    email: "",
    password: "",
    host: "",
    port: 993,
  });

  const [banner, setBanner] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  // Surface the OAuth callback result that the backend redirects back with
  // (e.g. /email-accounts?connected=gmail  or  /email-accounts?error=...).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const errCode = params.get("error");
    if (connected) {
      setBanner({ kind: "success", text: `${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully. Syncing will auto-track your applications.` });
    } else if (errCode) {
      const friendly: Record<string, string> = {
        consent_required: "Grant email access permission in Settings first, then reconnect.",
        invalid_or_expired_state: "The connection link expired. Please try connecting again.",
        access_denied: "You declined access at the provider. Connection cancelled.",
        google_token_failed: "Google rejected the connection. Check that this account is added as a test user.",
        microsoft_token_failed: "Microsoft rejected the connection. Verify the app registration and redirect URI.",
        verification_failed: "Connected, but we couldn't read your mailbox — the account was not activated. Please reconnect and grant inbox access.",
      };
      setBanner({ kind: "error", text: friendly[errCode] || `Could not connect: ${errCode}` });
    }
    if (connected || errCode) {
      // Clean the query string so a refresh doesn't re-show the banner.
      window.history.replaceState({}, "", "/email-accounts");
    }
  }, []);

  // Fetch email accounts and available providers
  useEffect(() => {
    axiosInstance.get("/auth/profile").then(r => {
      setIsPremium(r.data?.subscription?.isActive === true);
    }).catch(() => {});

    const fetchAccounts = async () => {
      try {
        const response = await axiosInstance.get("/auth/email/accounts");
        setEmailAccounts(response.data.emailAccounts || []);
        setError(null);

        // Fetch available providers
        const providersResponse = await axiosInstance.get("/auth/email/connect");
        setProviders(providersResponse.data.providers || []);
      } catch (err) {
        console.error("Fetch email accounts error:", err);
        setError("Failed to load email accounts");
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  // Handle OAuth provider selection
  const handleConnectOAuth = async (provider: EmailProvider) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/auth/email/connect?provider=${provider.id}`);
      window.location.href = response.data.authUrl;
    } catch (err) {
      console.error("Connect OAuth error:", err);
      setError(`Failed to initiate ${provider.name} connection`);
    } finally {
      setLoading(false);
    }
  };

  // Retry a failed/disconnected account. OAuth providers restart the consent
  // flow; IMAP providers re-open the credentials form so the user can fix them.
  const handleReconnect = async (account: EmailAccount) => {
    const prov = providers.find((p) => p.id === account.provider);
    if (prov && prov.type === "imap") {
      setSelectedProvider(prov);
      setImapFormData({ email: account.email && account.email.includes("@") ? account.email : "", password: "", host: "", port: 993 });
      setShowIMAPForm(true);
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/auth/email/connect?provider=${account.provider}`);
      window.location.href = response.data.authUrl;
    } catch (err) {
      console.error("Reconnect error:", err);
      setError(`Failed to reconnect ${account.provider}`);
      setLoading(false);
    }
  };

  // Handle IMAP provider selection
  const handleSelectIMAPProvider = (provider: EmailProvider) => {
    setSelectedProvider(provider);
    setShowIMAPForm(true);
    setShowProviderSelector(false);
  };

  // Handle IMAP form submission
  const handleConnectIMAP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProvider) {
      setError("No provider selected");
      return;
    }

    if (!imapFormData.email || !imapFormData.password) {
      setError("Email and password are required");
      return;
    }

    const emailCheck = validateEmail(imapFormData.email);
    if (!emailCheck.valid) {
      setError(emailCheck.message || "Enter a valid email address");
      return;
    }

    if (selectedProvider.id === "generic" && imapFormData.port && (imapFormData.port < 1 || imapFormData.port > 65535)) {
      setError("IMAP port must be between 1 and 65535");
      return;
    }

    if (selectedProvider.id === "generic" && (!imapFormData.host || !imapFormData.port)) {
      setError("IMAP host and port are required for generic provider");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/auth/email/callback",
        {
          provider: selectedProvider.id,
          email: imapFormData.email,
          password: imapFormData.password,
          imapHost: selectedProvider.id === "generic" ? imapFormData.host : undefined,
          imapPort: selectedProvider.id === "generic" ? imapFormData.port : undefined,
        }
      );

      // Add new account to list
      setEmailAccounts([...emailAccounts, response.data.emailAccount]);
      setShowIMAPForm(false);
      setImapFormData({ email: "", password: "", host: "", port: 993 });
      setSelectedProvider(null);
      setError(null);
      alert("Email account connected successfully!");
    } catch (err: any) {
      console.error("Connect IMAP error:", err);
      setError(err.response?.data?.error || "Failed to connect email account");
    } finally {
      setLoading(false);
    }
  };

  // Sync emails
  const handleSync = async (accountId: string) => {
    try {
      setSyncing(accountId);
      const response = await axiosInstance.post(
        "/auth/email/sync",
        { emailAccountId: accountId }
      );

      // Update last sync time
      setEmailAccounts((accounts: EmailAccount[]) =>
        accounts.map((acc: EmailAccount) =>
          acc.id === accountId
            ? { ...acc, lastSyncAt: new Date().toISOString() }
            : acc
        )
      );

      alert(
        `Synced ${response.data.syncedCount} email(s). ` +
          (response.data.updates.length > 0
            ? `${response.data.updates.length} application(s) updated.`
            : "No new status updates.")
      );

      setError(null);
    } catch (err: any) {
      if (err.response?.status === 403 && err.response?.data?.upgradeRequired) {
        setShowUpgradePrompt(true);
      } else {
        console.error("Sync error:", err);
        setError("Failed to sync emails");
      }
    } finally {
      setSyncing(null);
    }
  };

  // Disconnect account
  const handleDisconnect = async (accountId: string) => {
    if (!window.confirm("Disconnect this email account? Associated data will be deleted.")) {
      return;
    }

    try {
      await axiosInstance.delete(`/auth/email/accounts/${accountId}`);

      setEmailAccounts((accounts: EmailAccount[]) =>
        accounts.filter((acc: EmailAccount) => acc.id !== accountId)
      );

      setError(null);
    } catch (err) {
      console.error("Disconnect error:", err);
      setError("Failed to disconnect account");
    }
  };

  return (
    <div className="hb-email-container">
      {showUpgradePrompt && (
        <UpgradePrompt
          feature="email_sync"
          message="Manual email sync is a Pro feature. Upgrade to sync emails on demand and unlock automatic 30-minute background sync."
          onDismiss={() => setShowUpgradePrompt(false)}
        />
      )}

      <button
        onClick={() => navigate("/dashboard")}
        className="hb-btn hb-btn-secondary"
      >
        ← Back to Dashboard
      </button>

      {/* Coach Message */}
      <div className="hb-coach-message">
        <span className="hb-coach-icon">📧</span>
        <p>Connect your email to automatically track job applications!</p>
      </div>

      {/* Upgrade prompt for free users */}
      {!isPremium && (
        <div style={{ background: "linear-gradient(135deg, #eff6ff, #dbeafe)", border: "1px solid #93c5fd", borderRadius: "12px", padding: "16px 20px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "14px" }}>
          <span style={{ fontSize: "1.6rem" }}>👑</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#1e40af" }}>Upgrade to Premium</strong>
            <p style={{ margin: "2px 0 0", color: "#3b82f6", fontSize: "0.9rem" }}>
              Email monitoring automatically tracks replies, interview invites, and rejections straight from your inbox.
            </p>
          </div>
          <button
            onClick={() => navigate("/subscription")}
            style={{ background: "#2563eb", color: "white", border: "none", borderRadius: "8px", padding: "8px 18px", cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap" }}
          >
            Upgrade Now
          </button>
        </div>
      )}

      {/* Check email_access consent */}
      {!consents.email_access && (
        <DisabledFeature
          icon="📧"
          featureName="Email Integration"
          consentType="email_access"
          description="To use email integration for automatic job application tracking, you need to grant email access permission."
        />
      )}

      {consentsLoading ? (
        <div className="hb-email-card">
          <p className="hb-loading">⏳ Loading email accounts...</p>
        </div>
      ) : (
        <>
          {banner && (
            <div
              className={`hb-alert ${banner.kind === "success" ? "hb-alert-success" : "hb-alert-error"}`}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: 12, borderRadius: 8, marginBottom: 12,
                background: banner.kind === "success" ? "#dcfce7" : "#fef2f2",
                border: `1px solid ${banner.kind === "success" ? "#86efac" : "#fecaca"}`,
                color: banner.kind === "success" ? "#166534" : "#991b1b",
              }}
            >
              <span>{banner.kind === "success" ? "✅" : "⚠️"}</span>
              <p style={{ margin: 0 }}>{banner.text}</p>
              <button
                onClick={() => setBanner(null)}
                aria-label="Dismiss"
                style={{ marginLeft: "auto", background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "inherit" }}
              >×</button>
            </div>
          )}

          {error && (
            <div className="hb-alert hb-alert-error">
              <span>⚠️</span>
              <p>{error}</p>
            </div>
          )}

          {!showProviderSelector && !showIMAPForm && (
            <>
              {emailAccounts.length === 0 ? (
                <div className="hb-email-card hb-email-empty">
                  <div className="hb-empty-icon">📨</div>
                  <h2 className="hb-empty-title">No Email Accounts Connected</h2>
                  <p className="hb-empty-text">Connect an email account to automatically track your job applications</p>
                  <button
                    onClick={() => setShowProviderSelector(true)}
                    className="hb-btn hb-btn-primary"
                    disabled={loading}
                  >
                    ➕ Connect Email Account
                  </button>
                </div>
              ) : (
                <div className="hb-email-accounts">
                  <div className="hb-email-header">
                    <h2 className="hb-email-title">Connected Email Accounts</h2>
                    <button
                      onClick={() => setShowProviderSelector(true)}
                      className="hb-btn hb-btn-secondary"
                      disabled={loading}
                    >
                      ➕ Add Another Account
                    </button>
                  </div>

                  <div className="hb-email-accounts-grid">
                    {emailAccounts.map((account) => (
                      <div key={account.id} className="hb-email-account-card">
                        <div className="hb-email-account-header">
                          <div className="hb-email-provider">
                            {account.provider === "gmail" && <span className="hb-provider-icon">📧</span>}
                            {account.provider === "outlook" && <span className="hb-provider-icon">💼</span>}
                            {account.provider === "yahoo" && <span className="hb-provider-icon">📮</span>}
                            {account.provider === "icloud" && <span className="hb-provider-icon">🍎</span>}
                            {account.provider === "generic" && <span className="hb-provider-icon">✉️</span>}
                            <div>
                              <h3 className="hb-provider-name">
                                {account.provider === "gmail" && "Gmail"}
                                {account.provider === "outlook" && "Outlook"}
                                {account.provider === "yahoo" && "Yahoo"}
                                {account.provider === "icloud" && "iCloud"}
                                {account.provider === "generic" && "Other Email"}
                              </h3>
                              <p className="hb-email-address">
                                {account.email && account.email !== "connected" && account.email !== "pending"
                                  ? account.email
                                  : "Email pending fetch"}
                              </p>
                            </div>
                          </div>
                          <div className={`hb-status-badge ${account.isConnected ? 'hb-connected' : 'hb-disconnected'}`}>
                            {account.isConnected ? '✓ Connected' : '✗ Connection failed'}
                          </div>
                        </div>

                        <div className="hb-email-account-meta">
                          <p><strong>Connected:</strong> {new Date(account.createdAt).toLocaleDateString()}</p>
                          {account.lastSyncAt && (
                            <p><strong>Last sync:</strong> {formatTime(new Date(account.lastSyncAt))}</p>
                          )}
                        </div>

                        <div className="hb-email-account-actions">
                          {account.isConnected ? (
                            <button
                              onClick={() => handleSync(account.id)}
                              disabled={syncing === account.id}
                              className={syncing === account.id ? 'hb-btn hb-btn-primary hb-btn-loading' : 'hb-btn hb-btn-primary'}
                            >
                              {syncing === account.id ? '⏳ Syncing...' : '🔄 Sync Now'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReconnect(account)}
                              disabled={loading}
                              className="hb-btn hb-btn-primary"
                            >
                              🔁 Reconnect
                            </button>
                          )}
                          <button
                            onClick={() => handleDisconnect(account.id)}
                            className="hb-btn hb-btn-secondary"
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Section */}
              <div className="hb-email-info-section">
                <h2 className="hb-info-title">How It Works</h2>
                <div className="hb-info-steps">
                  <div className="hb-info-step">
                    <span className="hb-step-icon">1️⃣</span>
                    <div>
                      <h4>Connect Email</h4>
                      <p>Authorize Sumbitrbuddy to read your emails securely</p>
                    </div>
                  </div>
                  <div className="hb-info-step">
                    <span className="hb-step-icon">2️⃣</span>
                    <div>
                      <h4>Automatic Tracking</h4>
                      <p>We parse application-related emails for status updates</p>
                    </div>
                  </div>
                  <div className="hb-info-step">
                    <span className="hb-step-icon">3️⃣</span>
                    <div>
                      <h4>Smart Updates</h4>
                      <p>Job statuses are automatically updated when we detect changes</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy Section */}
              <div className="hb-email-privacy-section">
                <h2 className="hb-info-title">🔒 Privacy & Security</h2>
                <div className="hb-privacy-features">
                  <div className="hb-privacy-feature">
                    <span className="hb-privacy-icon">✓</span>
                    <div>
                      <h4>OAuth 2.0</h4>
                      <p>Industry-standard secure authentication</p>
                    </div>
                  </div>
                  <div className="hb-privacy-feature">
                    <span className="hb-privacy-icon">✓</span>
                    <div>
                      <h4>Encrypted IMAP</h4>
                      <p>Passwords encrypted at rest</p>
                    </div>
                  </div>
                  <div className="hb-privacy-feature">
                    <span className="hb-privacy-icon">✓</span>
                    <div>
                      <h4>Read-Only Access</h4>
                      <p>We cannot send, modify, or delete emails</p>
                    </div>
                  </div>
                  <div className="hb-privacy-feature">
                    <span className="hb-privacy-icon">✓</span>
                    <div>
                      <h4>Minimal Storage</h4>
                      <p>Only extracted data is stored</p>
                    </div>
                  </div>
                </div>
              </div>

            </>
          )}

          {/* Provider Selector Modal */}
          {showProviderSelector && (
            <div className="hb-modal-overlay">
              <div className="hb-email-provider-modal">
                <button
                  onClick={() => setShowProviderSelector(false)}
                  className="hb-modal-close"
                >
                  ✕
                </button>

                <h2 className="hb-modal-title">Connect Your Email</h2>
                <p className="hb-modal-subtitle">Choose your email provider</p>

                <div className="hb-provider-section">
                  <h3 className="hb-provider-section-title">Recommended - OAuth</h3>
                  <div className="hb-providers-grid hb-oauth-grid">
                    {providers.filter(p => p.type === "oauth").map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => handleConnectOAuth(provider)}
                        className="hb-provider-btn hb-provider-oauth"
                        disabled={loading}
                      >
                        <span className="hb-provider-icon">{provider.icon}</span>
                        <span className="hb-provider-btn-name">{provider.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="hb-provider-section">
                  <h3 className="hb-provider-section-title">Alternative - IMAP</h3>
                  <div className="hb-providers-grid hb-imap-grid">
                    {providers.filter(p => p.type === "imap").map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => handleSelectIMAPProvider(provider)}
                        className="hb-provider-btn hb-provider-imap"
                      >
                        <span className="hb-provider-icon">{provider.icon}</span>
                        <span className="hb-provider-btn-name">{provider.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* IMAP Form Modal */}
          {showIMAPForm && selectedProvider && (
            <div className="hb-modal-overlay">
              <div className="hb-email-imap-modal">
                <button
                  onClick={() => {
                    setShowIMAPForm(false);
                    setSelectedProvider(null);
                  }}
                  className="hb-modal-close"
                >
                  ✕
                </button>

                <h2 className="hb-modal-title">Connect {selectedProvider.name}</h2>
                <p className="hb-modal-subtitle">
                  {selectedProvider.id === "generic"
                    ? "Enter your email and IMAP server details"
                    : "Just your email address and an app password"}
                </p>

                <form onSubmit={(e) => handleConnectIMAP(e)} className="hb-imap-form">
                  <div className="hb-form-group">
                    <label htmlFor="email" className="hb-label">Email Address</label>
                    <input
                      id="email"
                      type="email"
                      value={imapFormData.email}
                      onChange={(e) =>
                        setImapFormData({ ...imapFormData, email: e.target.value })
                      }
                      placeholder="your@email.com"
                      required
                      className="hb-input"
                    />
                  </div>

                  <div className="hb-form-group">
                    <label htmlFor="password" className="hb-label">
                      {APP_PASSWORD_HELP[selectedProvider.id] ? "App Password" : "Password"}
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={imapFormData.password}
                      onChange={(e) =>
                        setImapFormData({ ...imapFormData, password: e.target.value })
                      }
                      placeholder={APP_PASSWORD_HELP[selectedProvider.id] ? "Your app password" : "Your password"}
                      required
                      className="hb-input"
                    />
                    {APP_PASSWORD_HELP[selectedProvider.id] ? (
                      <p className="hb-helper-text">
                        {APP_PASSWORD_HELP[selectedProvider.id].note}{" "}
                        <a
                          href={APP_PASSWORD_HELP[selectedProvider.id].url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Generate one →
                        </a>{" "}
                        It's encrypted and never stored in plain text.
                      </p>
                    ) : (
                      <p className="hb-helper-text">Your password is encrypted and never stored in plain text</p>
                    )}
                  </div>

                  {/* Host/port only for the generic "Other" provider — known
                      providers (Yahoo, iCloud, …) have their IMAP settings
                      filled in server-side, so the user never sees them. */}
                  {selectedProvider.id === "generic" ? (
                    <>
                      <div className="hb-form-group">
                        <label htmlFor="host" className="hb-label">IMAP Host</label>
                        <input
                          id="host"
                          type="text"
                          value={imapFormData.host}
                          onChange={(e) =>
                            setImapFormData({ ...imapFormData, host: e.target.value })
                          }
                          placeholder="imap.example.com"
                          required
                          className="hb-input"
                        />
                      </div>

                      <div className="hb-form-group">
                        <label htmlFor="port" className="hb-label">IMAP Port</label>
                        <input
                          id="port"
                          type="number"
                          value={imapFormData.port}
                          onChange={(e) =>
                            setImapFormData({
                              ...imapFormData,
                              port: parseInt(e.target.value),
                            })
                          }
                          placeholder="993"
                          required
                          className="hb-input"
                        />
                      </div>
                    </>
                  ) : null}

                  <button
                    type="submit"
                    disabled={loading}
                    className={loading ? 'hb-btn hb-btn-primary hb-btn-loading' : 'hb-btn hb-btn-primary'}
                  >
                    {loading ? '⏳ Connecting...' : '✅ Connect Account'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Helper function to format time
function formatTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  return "Just now";
}

export default EmailAccounts;
